import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { QualityBadge } from "./Badges";
import api, { apiError, money } from "@/lib/api";
import { Amount } from "@/components/shared/Amount";
import { useContent } from "@/context/ContentContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { detectCustomComments } from "@/lib/customComments";

export function OrderModal({ service, open, onClose, onPlaced }) {
  const { t } = useContent();
  const [target, setTarget] = useState("");
  const [quantity, setQuantity] = useState("");
  const [comments, setComments] = useState("");
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) { setTarget(""); setQuantity(service?.min_quantity || ""); setComments(""); api.get("/wallet").then((r) => setBalance(r.data.balance)); }
  }, [open, service]);

  if (!service) return null;
  const isCustomComments = detectCustomComments(service);
  const commentLines = comments.split("\n").map((l) => l.trim()).filter(Boolean);
  const qty = isCustomComments ? commentLines.length : (Number(quantity) || 0);
  const price = (service.price_per_1000 * qty) / 1000;
  const after = balance - price;
  const valid = qty >= service.min_quantity && qty <= service.max_quantity && target.length > 2 && after >= 0;

  const place = async () => {
    setLoading(true);
    try {
      const payload = { service_id: service.id, target, quantity: qty };
      if (isCustomComments) payload.comments = commentLines.join("\n");
      await api.post("/orders", payload);
      toast.success(t("ordermodal.created", "Commande créée avec succès"));
      onClose(); onPlaced && onPlaced();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const cleanComments = () => {
    const seen = new Set(); const out = [];
    for (const l of comments.split("\n")) { const v = l.trim(); if (v && !seen.has(v)) { seen.add(v); out.push(v); } }
    setComments(out.join("\n"));
  };

  const handleCommentsFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { const txt = String(reader.result || ""); setComments((p) => (p.trim() ? p.replace(/\n+$/, "") + "\n" : "") + txt); };
    reader.readAsText(file); e.target.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="order-modal">
        <DialogHeader><DialogTitle className="font-display">{t("ordermodal.title", "Nouvelle commande")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-secondary/40 p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{service.name}</span>
              <QualityBadge level={service.quality_level} />
            </div>
            <div className="mt-1 text-xs text-muted-foreground"><Amount value={service.price_per_1000} /> / 1 000 · {service.speed_description}</div>
          </div>
          <div>
            <label className="text-sm font-medium">{t("ordermodal.target_label", "Cible (URL / nom d'utilisateur)")}</label>
            <input value={target} onChange={(e) => setTarget(e.target.value)} data-testid="order-target" placeholder="https://..."
              className="mt-1 w-full rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/50" />
          </div>
          {isCustomComments ? (
            <div>
              <label className="text-sm font-medium">{t("client.no.comments_label", "Commentaires (1 par ligne)")} <span className="text-muted-foreground">({service.min_quantity.toLocaleString()} – {service.max_quantity.toLocaleString()})</span></label>
              <textarea value={comments} onChange={(e) => setComments(e.target.value)} data-testid="order-comments" rows={5}
                placeholder={t("client.no.comments_placeholder", "Collez vos commentaires, un par ligne…")}
                className="mt-1 w-full resize-y rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/50" />
              <div data-testid="order-comments-counter" className={`mt-1 text-xs ${qty < service.min_quantity || qty > service.max_quantity ? "text-red-600" : "text-muted-foreground"}`}>
                {qty.toLocaleString()} {t("client.no.comments_counter", "commentaire(s)")}
              </div>
              {comments.trim().length > 0 && (
                <button type="button" data-testid="order-comments-clean" onClick={cleanComments}
                  className="mt-2 inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                  {t("client.no.comments_clean", "Nettoyer (lignes vides + doublons)")}
                </button>
              )}
              <label data-testid="order-comments-import" className="ml-2 mt-2 inline-flex cursor-pointer items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                <input type="file" accept=".txt,text/plain" className="hidden" onChange={handleCommentsFile} />
                {t("client.no.comments_import", "Importer un .txt")}
              </label>
              {comments.length > 0 && (qty < service.min_quantity || qty > service.max_quantity) && (
                <div data-testid="order-comments-error" className="mt-1 text-xs text-red-600">
                  {t("client.no.comments_range", "Le nombre de commentaires doit être entre {min} et {max}.").replace("{min}", service.min_quantity.toLocaleString()).replace("{max}", service.max_quantity.toLocaleString())}
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="text-sm font-medium">{t("ordermodal.qty_label", "Quantité")} <span className="text-muted-foreground">({service.min_quantity.toLocaleString()} – {service.max_quantity.toLocaleString()})</span></label>
              <input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="order-quantity"
                className="mt-1 w-full rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/50" />
            </div>
          )}
          <div className="space-y-1.5 rounded-lg border border-border p-3 text-sm">
            <Row label={t("ordermodal.price", "Prix")} value={<Amount value={price} />} strong />
            <Row label={t("ordermodal.balance", "Solde actuel")} value={<Amount value={balance} />} />
            <Row label={t("ordermodal.after", "Solde après commande")} value={<Amount value={after} />} danger={after < 0} />
          </div>
          {after < 0 && <div className="text-xs text-red-600" data-testid="insufficient-balance">{t("ordermodal.insufficient", "Solde insuffisant. Ajoutez des fonds.")}</div>}
          <button disabled={!valid || loading} onClick={place} data-testid="place-order-btn"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent disabled:opacity-50">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("ordermodal.place", "Passer la commande")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, strong, danger }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={`${strong ? "font-display text-base font-bold" : "font-medium"} ${danger ? "text-red-600" : ""}`}>{value}</span>
    </div>
  );
}
